<template>
  <div class="template-storage-example">
    <h1>Template Storage Example</h1>
    
    <!-- Upload Section -->
    <section class="section">
      <h2>1. Upload Template</h2>
      
      <div v-if="!isAuthenticated" class="error-message">
        You are not authenticated. Please log in to upload templates.
      </div>
      
      <div class="form-group">
        <label for="file-upload">Select Excel Template:</label>
        <input
          type="file" 
          id="file-upload" 
          accept=".xlsx,.xls" 
          @change="handleFileChange"
        />
      </div>
      
      <div class="form-group">
        <label for="template-name">Template Name:</label>
        <input 
          type="text" 
          id="template-name" 
          v-model="templateName" 
          placeholder="Enter template name"
        />
      </div>
      
      <div class="form-group">
        <label for="template-description">Description:</label>
        <textarea 
          id="template-description" 
          v-model="templateDescription" 
          placeholder="Enter template description"
        ></textarea>
      </div>
      
      <button
        @click="uploadTemplate"
        :disabled="!selectedFile || !templateName || loading || !isAuthenticated"
        class="btn btn-primary"
      >
        {{ loading ? 'Uploading...' : 'Upload Template' }}
      </button>
      
      <div v-if="!selectedFile" class="error-message">
        Please select a file to upload.
      </div>
      
      <div v-if="!isAuthenticated" class="error-message">
        Please log in to upload templates.
      </div>
      
      <div v-if="error" class="error-message">
        {{ error.message }}
      </div>
    </section>
    
    <!-- Templates List Section -->
    <section class="section">
      <h2>2. My Templates</h2>
      
      <button @click="loadTemplates" class="btn btn-secondary">
        Refresh Templates
      </button>
      
      <div v-if="loading" class="loading">Loading templates...</div>
      
      <div v-else-if="templates.length === 0" class="empty-state">
        No templates found. Upload a template to get started.
      </div>
      
      <table v-else class="templates-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Uploaded</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="template in templates" :key="template.id">
            <td>{{ template.name }}</td>
            <td>{{ template.description }}</td>
            <td>
              <span :class="'status-badge status-' + template.status">
                {{ template.status }}
              </span>
            </td>
            <td>{{ formatDate(template.created_at) }}</td>
            <td class="actions">
              <button @click="downloadTemplate(template.id)" class="btn btn-sm">
                Download
              </button>
              <button @click="deleteTemplate(template.id)" class="btn btn-sm btn-danger">
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
    
    <!-- Latest Template Section -->
    <section class="section">
      <h2>3. Latest Template</h2>
      
      <button @click="loadLatestTemplate" class="btn btn-secondary">
        Get Latest Template
      </button>
      
      <div v-if="loading" class="loading">Loading latest template...</div>
      
      <div v-else-if="!selectedTemplate" class="empty-state">
        No template selected. Click "Get Latest Template" to load the most recent template.
      </div>
      
      <div v-else class="template-details">
        <h3>{{ selectedTemplate.name }}</h3>
        <p>{{ selectedTemplate.description }}</p>
        
        <div class="metadata">
          <div class="metadata-item">
            <strong>Status:</strong> {{ selectedTemplate.status }}
          </div>
          <div class="metadata-item">
            <strong>File Type:</strong> {{ selectedTemplate.file_type }}
          </div>
          <div class="metadata-item">
            <strong>File Size:</strong> {{ formatFileSize(selectedTemplate.file_size) }}
          </div>
          <div class="metadata-item">
            <strong>Original Filename:</strong> {{ selectedTemplate.original_filename }}
          </div>
          <div class="metadata-item">
            <strong>Uploaded:</strong> {{ formatDate(selectedTemplate.created_at) }}
          </div>
          <div class="metadata-item">
            <strong>Last Accessed:</strong> {{ formatDate(selectedTemplate.last_accessed) }}
          </div>
          <div class="metadata-item">
            <strong>Access Count:</strong> {{ selectedTemplate.access_count }}
          </div>
        </div>
        
        <div class="actions">
          <button @click="downloadTemplate(selectedTemplate.id)" class="btn btn-primary">
            Download Template
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useTemplateStorage } from '@/composables/useTemplateStorage';
import { useAuth } from '@/composables/useAuth';
import { logger, LogCategory } from '@/services/logger';

export default defineComponent({
  name: 'TemplateStorageExample',
  
  setup() {
    // Get the template storage composable
    const { 
      loading, 
      error, 
      templates, 
      selectedTemplate,
      uploadTemplate: uploadTemplateToStorage,
      getTemplates,
      getLatestTemplate,
      downloadTemplate: downloadTemplateFile,
      deleteTemplate: deleteTemplateById
    } = useTemplateStorage();
    
    // Form state
    const selectedFile = ref<File | null>(null);
    const templateName = ref('');
    const templateDescription = ref('');
    // Get the current authenticated user
    const { user, init, isAuthenticated } = useAuth();
    
    // Initialize authentication state
    init();
    
    // Use the current user's ID for the user_id field
    const currentUserId = computed(() => user.value?.id || '');
    
    // Log authentication status
    console.log('Authentication status:', isAuthenticated.value);
    if (user.value) {
      console.log('User ID:', user.value.id);
    } else {
      console.log('User not authenticated');
    }
    
    // Get the planner ID from the user's profile or generate a unique ID
    const plannerId = computed(() => {
      // If the user has a planner_id in their profile, use that
      if (user.value?.user_metadata?.planner_id) {
        return user.value.user_metadata.planner_id;
      }
      // Otherwise, use the user's ID as the planner ID
      return currentUserId.value;
    });
    
    // Handle file selection
    const handleFileChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        selectedFile.value = input.files[0];
        
        // Auto-fill name from filename if empty
        if (!templateName.value) {
          const filename = selectedFile.value.name;
          templateName.value = filename.substring(0, filename.lastIndexOf('.')) || filename;
        }
      }
    };
    
    // Upload template
    const uploadTemplate = async () => {
      if (!templateName.value) return;
      
      // Check if user is authenticated
      if (!isAuthenticated.value || !currentUserId.value) {
        console.log('Authentication check failed:', {
          isAuthenticated: isAuthenticated.value,
          currentUserId: currentUserId.value
        });
        
        // Try to initialize auth again
        await init();
        
        // Check again after initialization
        if (!isAuthenticated.value || !currentUserId.value) {
          alert('You must be logged in to upload templates. Please log in and try again.');
          return;
        }
      }
      
      try {
        // Check if a file is selected
        if (!selectedFile.value) {
          alert('Please select a file to upload.');
          return;
        }
        
        const fileToUpload = selectedFile.value;
        
        logger.info('Uploading template', LogCategory.STORAGE, {
          fileName: fileToUpload.name,
          fileSize: fileToUpload.size,
          fileType: fileToUpload.type,
          plannerId: plannerId.value
        });
        
        await uploadTemplateToStorage(
          fileToUpload,
          {
            name: templateName.value,
            description: templateDescription.value,
            user_id: currentUserId.value
          }
        );
        
        // Reset form
        selectedFile.value = null;
        templateName.value = '';
        templateDescription.value = '';
        
        // Reload templates
        await loadTemplates();
        
        alert('Template uploaded successfully!');
      } catch (err) {
        console.error('Upload failed:', err);
        
        // Display a more user-friendly error message
        let errorMessage = 'Failed to upload template.';
        
        if (err instanceof Error) {
          // Check for specific error types
          if (err.message.includes('User ID does not exist')) {
            errorMessage = 'User ID does not exist in the database. Please make sure you are logged in with a valid user account.';
            console.log('User ID error:', { userId: currentUserId.value, isAuthenticated: isAuthenticated.value });
            
            // Try to initialize auth again
            await init();
          } else if (err.message.includes('foreign key constraint')) {
            errorMessage = 'Database constraint error. Please make sure all required fields are valid.';
          } else if (err.message.includes('not authenticated')) {
            errorMessage = 'You are not authenticated. Please log in and try again.';
            
            // Try to initialize auth again
            await init();
          } else {
            errorMessage = err.message;
          }
        }
        
        alert(errorMessage);
      }
    };
    
    // Load templates
    const loadTemplates = async () => {
      try {
        await getTemplates();
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    };
    
    // Load latest template
    const loadLatestTemplate = async () => {
      try {
        const template = await getLatestTemplate();
        if (!template) {
          alert('No templates found.');
        }
      } catch (err) {
        console.error('Failed to load latest template:', err);
      }
    };
    
    // Download template
    const downloadTemplate = async (templateId: string) => {
      try {
        const fileBlob = await downloadTemplateFile(templateId);
        
        // Find the template to get the original filename
        const template = templates.value.find(t => t.id === templateId);
        const filename = template?.original_filename || 'template.xlsx';
        
        // Create a download link
        const url = URL.createObjectURL(fileBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download failed:', err);
        alert('Failed to download template: ' + (err instanceof Error ? err.message : String(err)));
      }
    };
    
    // Delete template
    const deleteTemplate = async (templateId: string) => {
      if (!confirm('Are you sure you want to delete this template?')) return;
      
      try {
        await deleteTemplateById(templateId);
        alert('Template deleted successfully!');
      } catch (err) {
        console.error('Delete failed:', err);
        alert('Failed to delete template: ' + (err instanceof Error ? err.message : String(err)));
      }
    };
    
    // Format date
    const formatDate = (dateString?: string | null) => {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    };
    
    // Format file size
    const formatFileSize = (bytes?: number | null) => {
      if (!bytes) return 'N/A';
      
      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unitIndex = 0;
      
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }
      
      return `${size.toFixed(2)} ${units[unitIndex]}`;
    };
    
    // Initialize on component mount
    onMounted(async () => {
      console.log('Component mounted, initializing auth...');
      await init();
      console.log('Auth initialized, authentication status:', isAuthenticated.value);
      if (user.value) {
        console.log('User ID after initialization:', user.value.id);
      } else {
        console.log('User still not authenticated after initialization');
      }
      
      // Load templates
      loadTemplates();
    });
    
    return {
      loading,
      error,
      templates,
      selectedTemplate,
      selectedFile,
      templateName,
      templateDescription,
      handleFileChange,
      uploadTemplate,
      loadTemplates,
      loadLatestTemplate,
      downloadTemplate,
      deleteTemplate,
      formatDate,
      formatFileSize,
      isAuthenticated
    };
  }
});
</script>

<style scoped>
.template-storage-example {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.section {
  margin-bottom: 40px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
}

h1 {
  font-size: 24px;
  margin-bottom: 20px;
}

h2 {
  font-size: 20px;
  margin-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

input[type="text"],
textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

textarea {
  height: 100px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.btn-primary {
  background-color: #4a90e2;
  color: white;
}

.btn-secondary {
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ccc;
}

.btn-danger {
  background-color: #e74c3c;
  color: white;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 12px;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-message {
  color: #e74c3c;
  margin-top: 10px;
  padding: 10px;
  background-color: #fadbd8;
  border-radius: 4px;
}

.loading {
  padding: 20px;
  text-align: center;
  color: #666;
}

.empty-state {
  padding: 20px;
  text-align: center;
  color: #666;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.templates-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}

.templates-table th,
.templates-table td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.templates-table th {
  background-color: #f0f0f0;
  font-weight: bold;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.status-pending {
  background-color: #f39c12;
  color: white;
}

.status-approved {
  background-color: #2ecc71;
  color: white;
}

.status-rejected {
  background-color: #e74c3c;
  color: white;
}

.actions {
  display: flex;
  gap: 5px;
}

.template-details {
  background-color: white;
  padding: 15px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.metadata {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin: 15px 0;
}

.metadata-item {
  padding: 5px;
}
</style>
